import { DOCUMENT } from '@angular/common';
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ArticleComponent } from '@modules/article/article.component';
import { ArticleModel } from '@modules/article/models/article.model';
import { RichSnippetFactory } from '@modules/rich-snippet/factories/rich-snippet.factory';
import { JsonLdService } from '@modules/rich-snippet/services/json-ld.service';
import { DataLayerService } from '@modules/shared/services/data-layer.service';
import { MetaTagsService } from '@modules/shared/services/meta-tags.service';
import { ActionIcons, ButtonItem } from '@modules/ui/action-container/action-container.component';

@Component({
  selector: 'mb-article-page',
  templateUrl: './article-page.component.html',
  styleUrls: ['./article-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, ArticleComponent],
})
export class ArticlePageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private dataLayerService = inject(DataLayerService);
  private metaTagsService = inject(MetaTagsService);
  private document = inject(DOCUMENT);
  private jsonLdService = inject(JsonLdService);
  private richSnippetFactory = inject(RichSnippetFactory);

  private destroy$ = new Subject<boolean>();
  public article: ArticleModel | null = null;

  public defaultActions: ButtonItem[] = [
    {
      icon: ActionIcons.SHARE,
      class: 'btn btn-plain d-none d-sm-inline-flex',
    },
    {
      icon: ActionIcons.DOWNLOAD,
      class: 'btn btn-plain d-none d-sm-inline-flex',
    },
    {
      icon: ActionIcons.BOOKMARK,
      class: 'btn btn-plain',
    },
  ];
  public articleActions: ButtonItem[] = [
    {
      icon: ActionIcons.SHARE,
      label: 'SHARE',
      class: 'btn btn-plain',
    },
    {
      icon: ActionIcons.DOWNLOAD,
      label: 'DOWNLOAD',
      class: 'btn btn-plain',
    },
    {
      icon: ActionIcons.BOOKMARK,
      label: 'SAVE',
      class: 'btn btn-plain',
    },
  ];
  public sectionActions: ButtonItem[] = [
    {
      icon: ActionIcons.DOWNLOAD,
      label: 'DOWNLOAD',
      class: 'btn btn-plain',
    },
  ];

  ngOnInit() {
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.article = data.article;
      if (!this.article) return;

      this.cdr.detectChanges();
      this.pushPageViewDataLayer();
      this.setArticleMetaTags();

      const richSnippet = this.richSnippetFactory.create('article').adapt(this.article);
      this.jsonLdService.addJsonLd(richSnippet, `article-${this.article.id}`);
    });
  }

  private setArticleMetaTags(): void {
    if (!this.article) return;

    const articleUrl = this.document.location.origin + this.document.location.pathname;
    const imageUrl = this.article.headerImage?.original?.url || undefined;
    const description = this.metaTagsService.extractDescription(this.article.content, 200);

    this.metaTagsService.setArticleMetaTags({
      title: this.article.title,
      url: articleUrl,
      image: imageUrl,
      description: description,
    });
  }

  private pushPageViewDataLayer() {
    if (!this.article) return;

    this.dataLayerService.pushPageView({
      id: this.article.id,
      type: 'press_information',
      name: this.article.title,
      publishDate: this.article.displayDate,
      marsOriginId: this.article.marsOriginId,
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.metaTagsService.removeArticleMetaTags();

    if (this.article) {
      this.jsonLdService.removeJsonLd(`article-${this.article.id}`);
    }
  }
}
